/* eslint-disable react/prop-types */
/* eslint-disable prefer-arrow-callback */
/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Dialog,
  Chip,
  Slide,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ArrowBack,
  LockOutlined,
  AccountBalanceWalletOutlined,
  PersonRemoveOutlined,
  PersonAddOutlined,
  VerifiedOutlined,
  StarRounded,
} from '@mui/icons-material';
import DashboardOutlined from '@mui/icons-material/DashboardOutlined';

// ─────────────────────────────────────────────
// SLIDE UP TRANSITION
// ─────────────────────────────────────────────
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─────────────────────────────────────────────
// FLOATING STAR CELEBRATION ELEMENT
// ─────────────────────────────────────────────
function FloatingStar({ delay, size, top, left, color, angle }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left,
        transform: `rotate(${angle}deg)`,
        opacity: 0,
        color,
        display: 'flex',
        animation: `starFloat 2.5s cubic-bezier(0.25, 1, 0.5, 1) ${delay}ms infinite`,
        '@keyframes starFloat': {
          '0%': { transform: `scale(0) rotate(${angle}deg)`, opacity: 0 },
          '15%': { opacity: 0.8 },
          '50%': { transform: `scale(1.2) translateY(-25px) rotate(${angle + 45}deg)`, opacity: 0.9 },
          '100%': { transform: `scale(0.6) translateY(-50px) rotate(${angle + 90}deg)`, opacity: 0 },
        },
      }}
    >
      <StarRounded sx={{ fontSize: size }} />
    </Box>
  );
}

// ─────────────────────────────────────────────
// ANIMATED SUCCESS ICON CIRCLE (GPay / PhonePe style with Gradient)
// ─────────────────────────────────────────────
function ActionIcon({ icon, gradient, resolvedBorder, show }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: 72,
        height: 72,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        my: 0.75,
      }}
    >
      {/* outer pulse ring */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          border: `2px solid ${alpha(resolvedBorder, 0.15)}`,
          animation: show ? 'outerPulse 2.4s ease-out 0.3s infinite' : 'none',
          '@keyframes outerPulse': {
            '0%': { transform: 'scale(1)', opacity: 0.8 },
            '100%': { transform: 'scale(1.55)', opacity: 0 },
          },
        }}
      />
      {/* mid pulse ring */}
      <Box
        sx={{
          position: 'absolute',
          inset: 8,
          borderRadius: '50%',
          border: `1.5px solid ${alpha(resolvedBorder, 0.25)}`,
          animation: show ? 'midPulse 2.4s ease-out 0.6s infinite' : 'none',
          '@keyframes midPulse': {
            '0%': { transform: 'scale(1)', opacity: 0.7 },
            '100%': { transform: 'scale(1.35)', opacity: 0 },
          },
        }}
      />
      {/* icon circle with rich gradient */}
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 8px 24px ${alpha(resolvedBorder, 0.3)}`,
          animation: show ? 'popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both' : 'none',
          '@keyframes popIn': {
            '0%': { transform: 'scale(0) rotate(-15deg)', opacity: 0 },
            '60%': { transform: 'scale(1.1) rotate(3deg)' },
            '100%': { transform: 'scale(1) rotate(0deg)', opacity: 1 },
          },
        }}
      >
        {icon === 'check' ? (
          <svg width="26" height="26" viewBox="0 0 40 40" fill="none">
            <path
              d="M10 21L17 28L30 13"
              stroke="#FFF"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="33"
              strokeDashoffset="33"
              style={{
                animation: show ? 'drawCheck 0.65s ease-out 0.45s forwards' : 'none',
              }}
            />
            <style>{`@keyframes drawCheck { to { stroke-dashoffset: 0; } }`}</style>
          </svg>
        ) : (
          React.cloneElement(icon, { sx: { fontSize: 26, color: '#FFF' } })
        )}
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────
// PAYMENT SUCCESS POPUP
// ─────────────────────────────────────────────
export function PaymentSuccessPopup({
  open,
  onClose,
  plan,
  paymentDetails,
  currency,
  billingCycle,
}) {
  const [visible, setVisible] = useState(false);
  const theme = useTheme();

  const symbol = currency === 'INR' ? '₹' : '$';

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => setVisible(true), 80);
      return () => clearTimeout(t);
    }
    setVisible(false);
    return undefined;
  }, [open]);

  if (!plan) return null;

  // ── Action type detection ──────────────────
  const isAddUsers = paymentDetails?.paymentMode === 'ADD_USERS';
  const isRemoveUsers = paymentDetails?.paymentMode === 'REMOVE_USERS';
  const isWalletOnly = paymentDetails?.walletPayment === true;
  const isAutoDebit = paymentDetails?.paymentMode === 'AUTO_DEBIT';

  // ── Amount display ─────────────────────────
  const fmtAmount = (n) =>
    Number(n || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const displayAmount = isRemoveUsers
    ? fmtAmount(paymentDetails?.walletUsed || paymentDetails?.walletCredited || 0)
    : fmtAmount(paymentDetails?.amount || 0);

  // ── Per-state config ───────────────────────
  const stateConfig = (() => {
    if (isRemoveUsers) {
      return {
        gradient: 'linear-gradient(135deg, #FF3B30 0%, #FF6B5C 100%)',
        iconBorder: theme.palette.error.light,
        iconEl: <PersonRemoveOutlined />,
        badgeBg: 'error.lighter',
        badgeColor: 'error.main',
        badgeBorder: 'error.light',
        badgeIcon: <VerifiedOutlined sx={{ fontSize: '13px' }} />,
        badgeText: 'Wallet Credited',
        headline: `${paymentDetails?.removeCount || ''} seat${paymentDetails?.removeCount > 1 ? 's' : ''} removed`,
        subline: 'Your team plan size has been adjusted.',
        thankYouTitle: 'Workspace Updated',
        thankYouBody: 'Prorated subscription credit has been calculated and returned directly to your organization wallet.',
        amtLabel: 'CREDITED TO WALLET',
        amtColor: 'success.main',
        amtPrefix: '+',
        accentColor: theme.palette.error.main,
      };
    }
    if (isAddUsers) {
      return {
        gradient: 'linear-gradient(135deg, #00B074 0%, #10D080 100%)',
        iconBorder: theme.palette.success.light,
        iconEl: <PersonAddOutlined />,
        badgeBg: 'success.lighter',
        badgeColor: 'success.main',
        badgeBorder: 'success.light',
        badgeIcon: <VerifiedOutlined sx={{ fontSize: '13px' }} />,
        badgeText: 'Seats Added',
        headline: `${paymentDetails?.userCount > 0 ? `${paymentDetails.userCount} New Seat${paymentDetails.userCount > 1 ? 's' : ''} Active` : 'Workspace Updated'}`,
        subline: 'New seats are immediately active and ready.',
        thankYouTitle: 'Teammates Invited!',
        thankYouBody: `Your new seats on the ${plan.name} plan are fully provisioned. Invite colleagues to join the collaboration space.`,
        amtLabel: 'TOTAL PAID',
        amtColor: 'success.main',
        amtPrefix: '',
        accentColor: theme.palette.success.main,
      };
    }
    if (isWalletOnly) {
      return {
        gradient: 'linear-gradient(135deg, #006BFF 0%, #00B2FF 100%)',
        iconBorder: theme.palette.primary.light,
        iconEl: <AccountBalanceWalletOutlined />,
        badgeBg: 'primary.lighter',
        badgeColor: 'primary.main',
        badgeBorder: 'primary.light',
        badgeIcon: <VerifiedOutlined sx={{ fontSize: '13px' }} />,
        badgeText: 'Paid via Wallet',
        headline: 'Wallet Payment Complete',
        subline: 'Amount deducted from your account wallet.',
        thankYouTitle: 'Uninterrupted Access Active',
        thankYouBody: `Your ${plan.name} plan subscription has been renewed using your organization wallet balance. No external cards were charged.`,
        amtLabel: 'DEDUCTED FROM WALLET',
        amtColor: 'primary.main',
        amtPrefix: '',
        accentColor: theme.palette.primary.main,
      };
    }
    return {
      gradient: 'linear-gradient(135deg, #00B074 0%, #10D080 100%)',
      iconBorder: theme.palette.success.light,
      iconEl: 'check',
      badgeBg: 'success.lighter',
      badgeColor: 'success.main',
      badgeBorder: 'success.light',
      badgeIcon: <VerifiedOutlined sx={{ fontSize: '13px' }} />,
      badgeText: isAutoDebit ? 'Auto-Debit Active' : 'Payment Success',
      headline: isAutoDebit ? 'Auto-Debit Active!' : 'Payment Complete!',
      subline: `Welcome to the ${plan.name} plan.`,
      thankYouTitle: `Thank you for choosing ${plan.name}!`,
      thankYouBody: 'Your account is upgraded and features are unlocked. We look forward to helping your organization scale effectively.',
      amtLabel: 'TOTAL PAID',
      amtColor: 'primary.main',
      amtPrefix: '',
      accentColor: theme.palette.success.main,
    };
  })();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      TransitionComponent={SlideUp}
      transitionDuration={{ enter: 400, exit: 250 }}
      PaperProps={{
        sx: {
          borderRadius: { xs: '28px 28px 0 0', sm: '24px' },
          overflow: 'hidden',
          m: { xs: 0, sm: 3 },
          mt: { xs: 'auto', sm: 3 },
          maxHeight: { xs: '96dvh', sm: 'calc(100% - 48px)' },
          bgcolor: 'background.paper',
          boxShadow: '0 24px 64px rgba(15, 17, 33, 0.25), 0 4px 16px rgba(15, 17, 33, 0.08)',
          border: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(12px)', bgcolor: 'rgba(15, 17, 33, 0.55)' },
        },
      }}
    >
      {/* ══ SCROLLABLE CONTENT WRAPPER ══ */}
      <Box
        sx={{
          overflowY: 'auto',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          '&::-webkit-scrollbar': { width: 3 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 4 },
          '@keyframes fadeInUp': {
            from: { opacity: 0, transform: 'translateY(12px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* ══ HEADER & ANIMATION CELEBRATION ══ */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pt: 2.5,
            pb: 1.5,
            px: 2.5,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            flexShrink: 0,
            background: `radial-gradient(circle at 50% 0%, ${alpha(stateConfig.accentColor, 0.12)} 0%, transparent 68%)`,
          }}
        >
          {/* Floating Celebration Sparkles (Wow effect) */}
          {visible && (
            <>
              <FloatingStar delay={200} size={11} top="15%" left="22%" color="warning.main" angle={15} />
              <FloatingStar delay={700} size={14} top="25%" left="72%" color="primary.main" angle={45} />
              <FloatingStar delay={400} size={13} top="40%" left="18%" color="success.main" angle={-30} />
              <FloatingStar delay={900} size={10} top="45%" left="78%" color="warning.light" angle={60} />
            </>
          )}

          <ActionIcon
            icon={stateConfig.iconEl}
            gradient={stateConfig.gradient}
            resolvedBorder={stateConfig.iconBorder}
            show={visible}
          />

          {/* Status chip */}
          <Chip
            icon={
              <Box sx={{ display: 'flex', color: `${stateConfig.badgeColor} !important` }}>
                {stateConfig.badgeIcon}
              </Box>
            }
            label={stateConfig.badgeText}
            size="small"
            sx={{
              mt: 0.5,
              mb: 0.75,
              fontSize: '0.65rem',
              fontWeight: 700,
              letterSpacing: 0.2,
              bgcolor: stateConfig.badgeBg,
              color: stateConfig.badgeColor,
              border: `1px solid ${stateConfig.badgeBorder}`,
              height: 22,
              px: 0.5,
              '& .MuiChip-icon': { ml: 0.5 },
              animation: visible ? 'fadeInUp 0.4s ease-out 0.6s both' : 'none',
            }}
          />

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1.15rem', sm: '1.25rem' },
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
              color: 'text.primary',
              textAlign: 'center',
              animation: visible ? 'fadeInUp 0.4s ease-out 0.7s both' : 'none',
            }}
          >
            {stateConfig.headline}
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              fontSize: '0.72rem',
              color: 'text.secondary',
              textAlign: 'center',
              maxWidth: 280,
              lineHeight: 1.35,
              fontWeight: 500,
              animation: visible ? 'fadeInUp 0.4s ease-out 0.8s both' : 'none',
            }}
          >
            {stateConfig.subline}
          </Typography>

          {/* ══ CENTRAL GPay / PhonePe AMOUNT PANEL ══ */}
          <Stack
            alignItems="center"
            spacing={0.15}
            sx={{
              mt: 2,
              pt: 1.5,
              borderTop: '1px dashed',
              borderColor: 'divider',
              width: '100%',
              animation: visible ? 'fadeInUp 0.4s ease-out 0.9s both' : 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.58rem',
                fontWeight: 750,
                color: 'text.disabled',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {stateConfig.amtLabel}
            </Typography>
            <Stack direction="row" alignItems="baseline" spacing={0.25}>
              <Typography
                sx={{
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  color: stateConfig.amtColor,
                  alignSelf: 'flex-start',
                  mt: 0.3,
                }}
              >
                {stateConfig.amtPrefix}{symbol}
              </Typography>
              <Typography
                sx={{
                  fontSize: '2rem',
                  fontWeight: 850,
                  color: stateConfig.amtColor,
                  letterSpacing: '-0.04em',
                  lineHeight: 1,
                }}
              >
                {displayAmount}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '4px',
                  bgcolor: alpha(stateConfig.accentColor, 0.1),
                  color: stateConfig.accentColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.62rem',
                }}
              >
                {plan.icon}
              </Box>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: 'text.secondary' }}>
                {plan.name} Plan · {billingCycle === 'annual' ? 'Annual' : 'Monthly'}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* ══ VIP CELEBRATORY CARD ══ */}
        <Box
          sx={{
            px: 2,
            pb: 1.5,
            pt: 0.25,
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
            animation: visible ? 'fadeInUp 0.4s ease-out 1.0s both' : 'none',
          }}
        >
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(stateConfig.accentColor, 0.04),
              border: '1.2px solid',
              borderColor: alpha(stateConfig.accentColor, 0.14),
              boxShadow: `0 4px 12px ${alpha(stateConfig.accentColor, 0.04)}`,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              textAlign: 'center',
              alignItems: 'center',
            }}
          >
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 800, color: 'text.primary' }}>
              {stateConfig.thankYouTitle}
            </Typography>

            <Typography
              sx={{
                fontSize: '0.7rem',
                color: 'text.secondary',
                lineHeight: 1.4,
                fontWeight: 450,
                maxWidth: 290,
              }}
            >
              {stateConfig.thankYouBody}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ══ BOTTOM TRUST & ACTIONS PANEL ══ */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
        {/* Trust line */}
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={0.5}
          sx={{
            mt: 2.25,
            mb: 2.25,
            animation: visible ? 'fadeInUp 0.38s ease-out 1.1s both' : 'none',
          }}
        >
          <VerifiedOutlined sx={{ fontSize: 12, color: 'success.main' }} />
          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', fontWeight: 600, letterSpacing: 0.2 }}>
            100% Secure & Encrypted · Secured by Razorpay
          </Typography>
        </Stack>

        {/* Action buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{
            px: 2,
            pt: 1,
            pb: 1.75,
            animation: visible ? 'fadeInUp 0.38s ease-out 1.2s both' : 'none',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
            onClick={onClose}
            sx={{
              borderRadius: '10px',
              fontWeight: 600,
              fontSize: '0.78rem',
              py: 0.8,
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'text.secondary' },
            }}
          >
            Back
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<DashboardOutlined sx={{ fontSize: 14 }} />}
            onClick={onClose}
            disableElevation
            sx={{
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.8rem',
              py: 0.8,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
              '&:hover': {
                boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
              },
            }}
          >
            Go to Dashboard
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}